import Clickable from 'components/Common/Clickable'
import Pane from 'components/Common/Pane'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import RadioButton from 'LuxKit/Radio/RadioButton'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import { useCallback } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import noop from 'lib/function/noop'
import { useIntl } from 'react-intl'

const FullHeightPane = styled(Pane)`
  width: ${rem(250)};
  height: 100%;

  ${mediaQueryUp.desktop} {
    width: unset;
    flex: 1;
  }
`

const DisclaimerLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.six};
`

const DisabledMessageBanner = styled.div`
  display: flex;
  align-items: center;
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.messaging.warning.lightBackground};
  border-radius: ${props => props.theme.borderRadius.S};
`

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  min-height: ${rem(20)};
`

interface Props {
  fareId: string
  isSelected: boolean
  name: string
  subtitle: React.ReactNode
  imageId: string
  onSelect: (fareId: string) => void
  disabled?: boolean
  disabledMessage?: string
}

function FareClassCard({ fareId, isSelected, name, subtitle, imageId, onSelect, disabled, disabledMessage, children }: React.PropsWithChildren<Props>) {
  const intl = useIntl()
  const onClick = useCallback(() => {
    if (!disabled) {
      onSelect(fareId)
    }
  }, [fareId, onSelect, disabled])

  return <Clickable onClick={disabled ? noop : onClick}>
    <FullHeightPane border={isSelected ? 'neutral-one' : 'neutral-five'} padding="24 16" background={disabled ? 'neutral-seven' : 'neutral-eight'}>
      <VerticalSpacer gap={16}>
        <Group direction="horizontal" gap={8}>
          <RadioButton checked={isSelected} disabled={disabled} />
          <VerticalSpacer gap={4}>
            <Heading variant="heading6">{name}</Heading>
            <SubtitleRow>{!disabled && subtitle}</SubtitleRow>
          </VerticalSpacer>
        </Group>
        <div>
          <ResponsiveImage
            id={imageId}
            aspectRatio="218:143"
            desktopAspectRatio="309:174"
          />
          <DisclaimerLabel>
            <Caption variant="medium" colour="neutral-two">
              {intl.formatMessage({
                id: '0XzuvSjlpyoEUzc4zxug',
                defaultMessage: 'Illustrative purposes only',
                description: 'Disclaimer label on fare class card image indicating it is for illustrative purposes',
              })}
            </Caption>
          </DisclaimerLabel>
        </div>
        {disabledMessage && <DisabledMessageBanner>
          <BodyText variant="small" colour="warning-light-contrast">{disabledMessage}</BodyText>
        </DisabledMessageBanner>}
        <div>
          {children}
        </div>
      </VerticalSpacer>
    </FullHeightPane>
  </Clickable>
}

export default FareClassCard
